import { PlanType } from "@brookbush/database";
import { UserAction } from "@brookbush/types";

import { SignedIn, SignedOut } from "@clerk/nextjs";
import type { PortableTextBlock } from "@portabletext/types";
import clsx from "clsx";
import Image from "next/image";
import Link from "next/link";
import type { ReactNode } from "react";

import SignupCard from "./SignupCard";
import TextBlock from "./TextBlock";
import { useAccess, useUserAction } from "~/hooks";
import useTrackCTA from "~/hooks/useTrackCTA";
import BecomeAMemberPlans from "~/pages/become-a-member/components/BecomeAMemberPlans";

interface HeroProps {
	title?: string;
	subtitle?: PortableTextBlock[];
	bulletPoints?: string[];
	imageUrl?: string;
	link?: {
		href: string;
		label: string;
	};
	isTeamMembership?: boolean;
	/** Optional content between the H1 and primary CTA (e.g. NCCA announcement). */
	announcement?: ReactNode;
}

const Hero = ({
	title,
	imageUrl,
	link,
	subtitle,
	bulletPoints,
	isTeamMembership,
	announcement,
}: HeroProps) => {
	const trackCTA = useTrackCTA();
	const { setUserAction } = useUserAction();
	const { isMember } = useAccess();

	const href = link?.href ?? "/sign-up";
	const opensSignupModal =
		!link?.href || href === "/sign-up" || href.startsWith("/sign-up?");
	const ctaClassName =
		"group flex items-center gap-2 rounded-xl bg-gradient-to-r from-sky-500 to-sky-700 px-4 py-2 text-lg text-white shadow-xl hover:brightness-95 sm:text-xl xl:px-5 xl:py-2.5";
	const ctaLabel = link?.label || "Sign Up";

	return (
		<section
			className={clsx(
				"relative flex flex-col items-center justify-center px-4 pt-20 pb-8 xl:h-[100vh] xl:flex-row xl:p-16",
				bulletPoints && "h-[100vh]",
			)}
			aria-label="Hero"
		>
			<div className="absolute inset-0 top-0 left-0 w-full overflow-hidden brightness-[0.4]">
				<Image
					loading="eager"
					fetchPriority="high"
					src={
						imageUrl ||
						"https://vczc4mkjnoo13s0s.public.blob.vercel-storage.com/homepage-cta-8szIwVX0TOm4hY87BGmfkfyZCuadsT.png"
					}
					placeholder="blur"
					blurDataURL="data:image/webp;base64,UklGRiIEAABXRUJQVlA4WAoAAAAgAAAAQgEApwAASUNDUMgBAAAAAAHIAAAAAAQwAABtbnRyUkdCIFhZWiAH4AABAAEAAAAAAABhY3NwAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAQAA9tYAAQAAAADTLQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAlkZXNjAAAA8AAAACRyWFlaAAABFAAAABRnWFlaAAABKAAAABRiWFlaAAABPAAAABR3dHB0AAABUAAAABRyVFJDAAABZAAAAChnVFJDAAABZAAAAChiVFJDAAABZAAAAChjcHJ0AAABjAAAADxtbHVjAAAAAAAAAAEAAAAMZW5VUwAAAAgAAAAcAHMAUgBHAEJYWVogAAAAAAAAb6IAADj1AAADkFhZWiAAAAAAAABimQAAt4UAABjaWFlaIAAAAAAAACSgAAAPhAAAts9YWVogAAAAAAAA9tYAAQAAAADTLXBhcmEAAAAAAAQAAAACZmYAAPKnAAANWQAAE9AAAApbAAAAAAAAAABtbHVjAAAAAAAAAAEAAAAMZW5VUwAAACAAAAAcAEcAbwBvAGcAbABlACAASQBuAGMALgAgADIAMAAxADZWUDggNAIAALAbAJ0BKkMBqAA+0WKqUTq0rSKjU1sLUBoJaW7gBgH/+R36m+v/O/j4bip2AJr2c7wkYDVXdpAURTjC8YigBvLjO08ZUxvaK61NYVGngK/m6YgHrKmBjuFZc0ADJigJsC6BQMxlSX2xkePie9/mFwOdWUOHGdlAyEF0hMmMWtlMFvWewLnAekM9xPlWHrzrDpTfPS9qsocQB7f1S13Y2OFE7NecYhc4DQnPaOtvXIIYzpLjJMuSgRAztgda0TxOhe97kRCXCTLstQ0BM9W34RhKER/C4V6c0AJlqO+8h1/Hb5kjoAXAAP7hx3WtIsO2iBJso1AZwqsj4GLV1wMOXA2K6UyIzx138av/qM4M19HW8Y33mN5xpxyHlRO0u96qOYILi1FeT2FvTGTvyRZHu0U0ex9yKpSvJttx8p3v7IPyP4GfoCTN7ZcukECjVaTrOF0eGMfkYom0ZPsSXj2on5mjm0OoMLXmqD2tnH1d8LBxpbuBqQEqi4kXLFbiXgB4wVvvu6brsXVpZA3ZM4vVfv0I5AUt5WE510mcbbyxL+HyRG2ggXI0B8Ezdmp5ZtwoWL/1fDpVIq7ehBUdMa4sguilFoOQGCLnNq4sKTu0kGyJ3yucdFuWS5k1UDAoj+VOPVbD/X23pEnJjBYWxTBJ8RGn5T3pnjqVI4rIN+hK5byr9AAhxpqQI/WMap0Y8W0kEd1JI8m4g4/8rLNBgEgS0B3kLbD/rt5QNVxbd7Or2LAAAAAAAA=="
					fill
					quality={60}
					decoding="async"
					alt="background image"
					className="object-cover"
				/>
			</div>

			<div
				className={clsx(
					"relative flex h-full w-full flex-1 flex-col justify-center gap-8 text-left xl:p-8",
					bulletPoints && "items-start",
					!bulletPoints && "items-center",
				)}
			>
				<h1
					className={clsx(
						"font-semibold text-white tracking-tight",
						subtitle || announcement
							? "text-2xl sm:text-3xl lg:text-4xl xl:text-5xl"
							: "text-3xl md:text-4xl lg:text-6xl",
						bulletPoints && "text-left",
						!bulletPoints && "mx-auto text-center",
					)}
				>
					{title}
				</h1>

				{announcement}

				{subtitle && (
					<TextBlock
						value={subtitle}
						className={clsx(
							"dark-text-block dark",
							bulletPoints && "text-left",
							!bulletPoints && "text-center",
						)}
					/>
				)}

				{bulletPoints && (
					<div className="flex flex-col gap-4 sm:flex-row">
						<ul className="ml-4 list-disc text-lg">
							{bulletPoints.map((bulletPoint) => (
								<li key={bulletPoint} className="text-white">
									{bulletPoint}
								</li>
							))}
						</ul>
					</div>
				)}

				<div className="flex flex-col gap-4 sm:flex-row">
					<div
						className={clsx(
							"flex flex-col gap-3",
							bulletPoints ? "items-start" : "items-center",
						)}
					>
						{opensSignupModal ? (
							<button
								type="button"
								onClick={() => {
									trackCTA(UserAction.SIGN_UP, "Hero Button");
									setUserAction(UserAction.SIGN_UP);
								}}
								className={ctaClassName}
							>
								{ctaLabel}
							</button>
						) : (
							<Link href={href} className={ctaClassName}>
								{ctaLabel}
							</Link>
						)}

						<SignedOut>
							<Link
								href="/sign-in"
								className="group mt-2 flex items-center gap-2 whitespace-nowrap text-lg text-white hover:underline"
							>
								Already a member? Sign in
							</Link>
						</SignedOut>
					</div>
				</div>
			</div>

			<div className="relative hidden h-full flex-1 items-center justify-center xl:flex">
				<SignedOut>
					<SignupCard isTeamMembership={false} />
				</SignedOut>

				{!isMember && (
					<SignedIn>
						<BecomeAMemberPlans
							landingPage={
								isTeamMembership ? PlanType.TEAM : PlanType.INDIVIDUAL
							}
						/>
					</SignedIn>
				)}
			</div>
		</section>
	);
};

export default Hero;
